import React, {FC, useCallback, useEffect, useRef, useState} from 'react'
import {useFormikContext} from 'formik'
import QrCodeScanner from '../../../../components/QrCodeScanner/QrCodeScanner'
import {rolnaDetaljiProizvodnja} from '../../../../api/fmkNalozi'
import {fetchTable} from '../../../../api/tableRS'
import {FmkNalogiStavkeModel} from '../../../../models/FmkNaloziModel'
import type {MaterijalTip, StartMaterijalRow, ZaduzenjeModalData} from './Step3'
import {matchesTrakaWidth, parseTrakaWidth} from './trakaWidth'
import {
    calculatePotrebnaKolicina, checkZaduzenje, extractMaterialId, getResponseData,
    getStavkaId, getZaduzenjeUposlenik, normalizeScan,
} from './materijalHelpers'

interface Props {
    row: StartMaterijalRow
    index: number
    tip: MaterijalTip
    stavke: FmkNalogiStavkeModel['stavka']
    stavkaInfo: Record<string, any>
    infoReady: boolean
    onZaduzenje: (data: ZaduzenjeModalData) => void
    onRemove: () => void
    canRemove: boolean
}

const clearedCalculation = {
    Provjeren: false,
    PotrebnaKolicina: '' as const,
    PotrebneKolicinePoStavci: {},
    NormativPoruka: '',
}

const StartMaterijalCard: FC<Props> = ({row, index, tip, stavke, stavkaInfo, infoReady, onZaduzenje, onRemove, canRemove}) => {
    const {setFormikState} = useFormikContext<{materijali: StartMaterijalRow[]}>()
    const [details, setDetails] = useState<any>(null)
    const [retry, setRetry] = useState(0)
    const [materialLoading, setMaterialLoading] = useState(false)
    const [manualSelection, setManualSelection] = useState(false)
    const manualSelectionRef = useRef(false)
    const materialVersion = useRef(0)
    const requestVersion = useRef(0)
    const normativCache = useRef(new Map<string, ReturnType<typeof calculatePotrebnaKolicina>>())
    const {RowId, MaterijalId, StavkaIds} = row
    const updateRow = useCallback((patch: Partial<StartMaterijalRow>) => {
        // Merge against the current state even when several API responses finish together.
        setFormikState((state) => ({
            ...state,
            values: {
                ...state.values,
                materijali: state.values.materijali.map((item) => item.RowId === RowId ? {...item, ...patch} : item),
            },
        }))
    }, [RowId, setFormikState])

    // Load material before selecting items; selection changes never refetch it.
    useEffect(() => {
        let active = true
        const version = ++materialVersion.current
        const isCurrent = () => active && version === materialVersion.current
        setDetails(null)
        normativCache.current.clear()
        setMaterialLoading(Boolean(MaterijalId))
        updateRow({...clearedCalculation, Ucitavanje: Boolean(MaterijalId)})
        if (!MaterijalId) return

        const timer = window.setTimeout(async () => {
            try {
                if (!/^\d+$/.test(MaterijalId) || Number(MaterijalId) <= 0) {
                    throw new Error('Upišite ispravan broj materijala.')
                }
                const zaduzenje = await checkZaduzenje(MaterijalId, tip)
                if (!isCurrent()) return
                if (zaduzenje) {
                    onZaduzenje({tip, uposlenik: getZaduzenjeUposlenik(zaduzenje)})
                    throw new Error('Materijal je već zadužen. Odaberite drugi materijal ili prvo uradite promet.')
                }
                const response = tip === 'rolna'
                    ? await rolnaDetaljiProizvodnja({rolnaId: MaterijalId})
                    : await fetchTable(Number(MaterijalId))
                if (!isCurrent()) return
                const material = getResponseData(response)
                if (!material || material.message) throw new Error(material?.message || 'Materijal nije pronađen.')
                setDetails(material)
                if (!manualSelectionRef.current) {
                    const matched = tip === 'traka'
                        ? stavke.filter((stavka: any) => matchesTrakaWidth(stavka.NazivArtikla || stavka.Naziv, material.Sirina))
                        : stavke.length === 1 ? stavke : []
                    updateRow({StavkaIds: matched.map((stavka) => String(getStavkaId(stavka)))})
                }
            } catch (error: any) {
                if (isCurrent()) updateRow({
                    ...clearedCalculation,
                    NormativPoruka: error?.response?.data?.message ?? error?.message ?? 'Greška pri učitavanju materijala.',
                })
            } finally {
                if (isCurrent()) {
                    setMaterialLoading(false)
                    updateRow({Ucitavanje: false})
                }
            }
        }, 350)
        return () => {
            active = false
            window.clearTimeout(timer)
        }
    }, [MaterijalId, onZaduzenje, retry, stavke, tip, updateRow])

    useEffect(() => {
        let active = true
        const version = ++requestVersion.current
        const isCurrent = () => active && version === requestVersion.current
        if (!details || materialLoading) return
        const assigned = stavke.filter((stavka) => StavkaIds.includes(String(getStavkaId(stavka))))
        updateRow({...clearedCalculation, Ucitavanje: Boolean(assigned.length && infoReady)})
        if (!assigned.length || !infoReady) return

        // Group quick taps; reuse successful amounts when only the selection changes.
        const timer = window.setTimeout(async () => {
            try {
                const amounts = await Promise.all(assigned.map((stavka) => {
                    const key = JSON.stringify([MaterijalId, stavka, stavkaInfo[String(getStavkaId(stavka))]])
                    let amount = normativCache.current.get(key)
                    if (!amount) {
                        amount = calculatePotrebnaKolicina(details, tip, [stavka], stavkaInfo)
                        normativCache.current.set(key, amount)
                        amount.catch(() => {
                            if (normativCache.current.get(key) === amount) normativCache.current.delete(key)
                        })
                    }
                    return amount
                }))
                if (!isCurrent()) return
                updateRow({
                    Provjeren: true,
                    PotrebnaKolicina: amounts.reduce((sum, amount) => sum + amount.total, 0),
                    PotrebneKolicinePoStavci: Object.assign({}, ...amounts.map((amount) => amount.poStavci)),
                    NormativPoruka: Array.from(new Set(amounts.map((amount) => amount.warning).filter(Boolean))).join(' '),
                    Ucitavanje: false,
                })
            } catch (error: any) {
                if (isCurrent()) updateRow({
                    ...clearedCalculation,
                    Ucitavanje: false,
                    NormativPoruka: error?.response?.data?.message ?? error?.message ?? 'Greška pri obračunu normativa.',
                })
            }
        }, 200)
        return () => {
            active = false
            window.clearTimeout(timer)
        }
    }, [MaterijalId, StavkaIds, details, infoReady, materialLoading, stavkaInfo, stavke, tip, updateRow])

    const changeAssignments = (ids: string[]) => {
        ++requestVersion.current
        manualSelectionRef.current = true
        setManualSelection(true)
        updateRow({...clearedCalculation, StavkaIds: ids})
    }
    const changeNumber = (raw: string, scanned = false) => {
        const cleaned = normalizeScan(raw)
        const id = extractMaterialId(cleaned)
        if (id !== MaterijalId) {
            ++requestVersion.current
            ++materialVersion.current
            manualSelectionRef.current = false
            setManualSelection(false)
            setDetails(null)
        }
        updateRow({
            ...(id !== MaterijalId ? {...clearedCalculation, StavkaIds: []} : {}),
            Raw: raw,
            MaterijalId: id,
            Skenirano: scanned || cleaned.includes(';') ? 1 : 0,
        })
    }
    const typeLabel = tip === 'rolna' ? 'rolne' : 'trake'
    const width = parseTrakaWidth(details?.Sirina)
    const matchingIds = tip === 'traka' && details
        ? stavke.filter((stavka: any) => matchesTrakaWidth(stavka.NazivArtikla || stavka.Naziv, details.Sirina)).map((stavka) => String(getStavkaId(stavka)))
        : []

    return (
        <section className='card shadow-sm start-material-card' aria-label={`${tip === 'rolna' ? 'Rolna' : 'Traka'} ${index + 1}`}>
            <div className='card-body p-4 p-md-5'>
                <div className='d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4'>
                    <h3 className='mb-0 text-break'>{tip === 'rolna' ? 'Rolna' : 'Traka'} {index + 1}{MaterijalId ? ` — ${MaterijalId}` : ''}</h3>
                    <button type='button' className='btn btn-light-danger' disabled={!canRemove} onClick={onRemove}>
                        Ukloni {tip === 'rolna' ? 'rolnu' : 'traku'}
                    </button>
                </div>
                <div className='d-flex flex-wrap align-items-center gap-3 mb-4'>
                    <span role='status' aria-label='Status skeniranja'
                        className={`badge ${row.Skenirano ? 'bg-success' : 'bg-danger'} d-inline-flex align-items-center gap-2 px-3 py-2 fs-6`}>
                        <i aria-hidden='true' className={`bi ${row.Skenirano ? 'bi-check-circle-fill' : 'bi-x-circle-fill'}`} />
                        {row.Skenirano ? 'Skenirano' : 'Nije skenirano'}
                    </span>
                    {!row.Skenirano && <span className='text-muted'>Skenirajte materijal. Ručni unos koristite samo iznimno.</span>}
                </div>
                <div className='d-flex gap-3 align-items-end'>
                    <div className='flex-grow-1' style={{minWidth: 0}}>
                        <label htmlFor={`materijal-${RowId}`} className='form-label fw-bold'>Broj {typeLabel}</label>
                        <input id={`materijal-${RowId}`} className='form-control form-control-lg' inputMode='numeric' autoComplete='off'
                            placeholder={`Upišite broj ${typeLabel}`} value={row.Raw}
                            onChange={(event) => changeNumber(event.target.value)}
                            onKeyDown={(event) => {if (event.key === 'Enter') event.preventDefault()}} />
                    </div>
                    <QrCodeScanner buttonLabel='Skeniraj' onScan={(raw) => changeNumber(raw, true)} />
                </div>
                {renderMaterialInfo(details, materialLoading, tip)}
                {details && tip === 'traka' && (
                    <div className={`alert ${matchingIds.length ? 'alert-primary' : 'alert-warning'} mt-4 mb-0`} role='status'>
                        {manualSelection ? 'Odabir ste ručno izmijenili.' : matchingIds.length
                            ? `Automatski označeno po širini ${width} mm: ${matchingIds.length} stavki. Provjerite odabir ispod.`
                            : width === null
                                ? 'Traka nema podatak o širini. Označite stavke ručno.'
                                : `Širina trake je ${width} mm. Nema stavki s tom mjerom u nazivu. Označite stavke ručno.`}
                        {manualSelection && matchingIds.length > 0 && (
                            <button type='button' className='btn btn-light-primary w-100 mt-3' onClick={() => {
                                changeAssignments(matchingIds)
                                manualSelectionRef.current = false
                                setManualSelection(false)
                            }}>Označi po širini {width} mm</button>
                        )}
                    </div>
                )}
                {details && (
                    <fieldset className='mt-5'>
                        <legend className='fs-5 fw-bold'>Stavke za ovu {tip === 'rolna' ? 'rolnu' : 'traku'} ({StavkaIds.length}/{stavke.length})</legend>
                        <div className='text-muted mb-3'>Dodirnite stavku da je označite ili uklonite oznaku.</div>
                        {stavke.length > 1 && (
                            <label className='d-flex align-items-center gap-3 p-4 mb-2 rounded border bg-light cursor-pointer'>
                                <input type='checkbox' className='form-check-input m-0 flex-shrink-0'
                                    checked={stavke.every((stavka) => StavkaIds.includes(String(getStavkaId(stavka))))}
                                    onChange={(event) => changeAssignments(event.target.checked ? stavke.map((stavka) => String(getStavkaId(stavka))) : [])} />
                                <span className='fw-bold'>Sve stavke</span>
                            </label>
                        )}
                        <div className='row g-3'>
                            {stavke.map((stavka: any, stavkaIndex) => {
                                const id = String(getStavkaId(stavka))
                                const checked = StavkaIds.includes(id)
                                return (
                                    <div key={id} className='col-12 col-md-6'>
                                        <label className={`d-flex align-items-start gap-3 p-4 border rounded cursor-pointer h-100 ${checked ? 'border-primary bg-light-primary' : ''}`}>
                                            <input type='checkbox' className='form-check-input m-0 mt-1 flex-shrink-0' checked={checked}
                                                onChange={(event) => changeAssignments(event.target.checked ? [...StavkaIds, id] : StavkaIds.filter((item) => item !== id))} />
                                            <span className='text-break' style={{minWidth: 0}}>
                                                <span className='d-block fw-bold'>{stavkaIndex + 1}. {stavka.NazivArtikla || stavka.Naziv}</span>
                                                <span className='d-block mt-1'>Količina: {stavka.Kolicina} {stavka.Jm ?? stavka.IdJedMjere}</span>
                                                <span className='d-block text-muted fs-7'>Šifra: {stavka.SifraArtikla ?? stavka.IdArtikla} · FMK: {stavka.fmkId ?? stavka.FmkId ?? id}</span>
                                            </span>
                                        </label>
                                    </div>
                                )
                            })}
                        </div>
                        {!StavkaIds.length && <div className='text-primary mt-3'>Označite najmanje jednu stavku za ovaj materijal.</div>}
                        <div className='mt-4'>
                            <label htmlFor={`kolicina-${RowId}`} className='form-label fw-semibold'>Potrebna količina za označene stavke</label>
                            <input id={`kolicina-${RowId}`} readOnly className='form-control form-control-lg form-control-solid fw-bold text-primary'
                                value={row.PotrebnaKolicina === '' ? '—' : row.PotrebnaKolicina} />
                        </div>
                    </fieldset>
                )}
                {row.NormativPoruka && (
                    <div className={`alert ${row.Provjeren ? 'alert-warning' : 'alert-danger'} mt-4 mb-0`} role='alert'>
                        {row.NormativPoruka}
                        {row.Provjeren && <div className='mt-2'>Možete nastaviti i pokrenuti nalog. Za stavke bez normativa potrebna količina je postavljena na 0.</div>}
                        <button type='button' className='btn btn-light w-100 mt-3' onClick={() => {
                            ++requestVersion.current
                            ++materialVersion.current
                            setDetails(null)
                            updateRow(clearedCalculation)
                            setRetry((value) => value + 1)
                        }}>Pokušaj ponovo</button>
                    </div>
                )}
                {!materialLoading && row.Ucitavanje && <div role='status' className='mt-4'>Obračun količine… <span className='spinner-border spinner-border-sm ms-2' /></div>}
                {row.Provjeren && <div className='text-success fw-bold mt-4' role='status'>Materijal je spreman za označene stavke.</div>}
            </div>
        </section>
    )
}

export {StartMaterijalCard}

const renderMaterialInfo = (info: any, loading: boolean, tip: MaterijalTip) => {
    if (loading) return <div className='alert alert-light border mt-4 mb-0' role='status'>Učitavanje materijala… <span className='spinner-border spinner-border-sm ms-2' /></div>
    if (!info) return <div className='text-muted mt-3'>Skenirajte materijal da učitate detalje i odaberete stavke. Po potrebi broj možete upisati ručno.</div>
    const entries = tip === 'traka' ? [
        ['Širina', info.Sirina != null ? `${info.Sirina} mm` : 'N/A'],
        ['Dužina', info.TrenutnaDuzina ?? 'N/A'],
        ['Težina', info.Tezina ?? 'N/A'],
        ['Rolna', info.BrojRolne ?? 'N/A'],
        ['Proizvođač', info.repromaterijal?.proizvodjac?.PartnerNaziv ?? 'N/A'],
    ] : [
        ['Širina', info.ReprMatSirina != null ? `${info.ReprMatSirina} mm` : 'N/A'],
        ['Težina / Količina', info.ReprMatTrenutnaKolicina ?? 'N/A'],
        ['Debljina', info.ReprMatDebljina ?? 'N/A'],
        ['Boja', info.boja_lica?.BojaNaziv ?? 'N/A'],
        ['Proizvođač', info.proizvodjac?.PartnerNaziv ?? 'N/A'],
    ]
    return (
        <dl className='row g-3 bg-light rounded p-3 mt-4 mb-0'>
            {entries.map(([label, value]) => (
                <div key={label} className='col-6 col-md-4 text-break'>
                    <dt className='text-muted fw-normal fs-7'>{label}</dt>
                    <dd className={`mb-0 fw-bold ${label === 'Širina' ? 'text-primary fs-4' : ''}`}>{value}</dd>
                </div>
            ))}
        </dl>
    )
}
